import React, { createContext, useContext, useState, useMemo } from 'react';
import { getDefaultStyles } from '../utils/styleUtils';

const StyleContext = createContext();

export const StyleProvider = ({ children }) => {
  const [customStyles, setCustomStyles] = useState(null);

  // Memoize merged styles to prevent unnecessary re-renders
  const styles = useMemo(() => {
    const defaults = getDefaultStyles();
    if (!customStyles) return defaults;
    
    return {
      colors: { ...defaults.colors, ...(customStyles.colors || {}) },
      typography: {
        ...defaults.typography,
        ...(customStyles.typography || {}),
        fontSizes: { ...defaults.typography.fontSizes, ...customStyles.typography?.fontSizes },
        fontWeights: { ...defaults.typography.fontWeights, ...customStyles.typography?.fontWeights },
        lineHeight: { ...defaults.typography.lineHeight, ...customStyles.typography?.lineHeight },
        letterSpacing: { ...defaults.typography.letterSpacing, ...customStyles.typography?.letterSpacing },
      },
      spacing: {
        ...defaults.spacing,
        ...(customStyles.spacing || {}),
        padding: { ...defaults.spacing.padding, ...customStyles.spacing?.padding },
        margin: { ...defaults.spacing.margin, ...customStyles.spacing?.margin },
        gap: { ...defaults.spacing.gap, ...customStyles.spacing?.gap },
      },
      borderRadius: { ...defaults.borderRadius, ...(customStyles.borderRadius || {}) },
      borderWidth: { ...defaults.borderWidth, ...(customStyles.borderWidth || {}) },
      borderStyle: { ...defaults.borderStyle, ...(customStyles.borderStyle || {}) },
      shadows: { ...defaults.shadows, ...(customStyles.shadows || {}) },
      transitions: {
        ...defaults.transitions,
        ...(customStyles.transitions || {}),
        properties: { ...defaults.transitions.properties, ...customStyles.transitions?.properties },
      },
      components: customStyles.components ? {
        paymentOption: { ...defaults.components.paymentOption, ...(customStyles.components.paymentOption || {}) },
        radioButton: { ...defaults.components.radioButton, ...(customStyles.components.radioButton || {}) },
        input: { ...defaults.components.input, ...(customStyles.components.input || {}) },
        button: {
          ...defaults.components.button,
          primary: { ...defaults.components.button.primary, ...(customStyles.components.button?.primary || {}) },
        },
        badge: { ...defaults.components.badge, ...(customStyles.components.badge || {}) },
        label: { ...defaults.components.label, ...(customStyles.components.label || {}) },
        errorMessage: { ...defaults.components.errorMessage, ...(customStyles.components.errorMessage || {}) },
        loader: { ...defaults.components.loader, ...(customStyles.components.loader || {}) },
        paymentIcon: { ...defaults.components.paymentIcon, ...(customStyles.components.paymentIcon || {}) },
      } : defaults.components,
      layout: { ...defaults.layout, ...(customStyles.layout || {}) },
      breakpoints: { ...defaults.breakpoints, ...(customStyles.breakpoints || {}) },
    };
  }, [customStyles]);

  const updateCustomStyles = (newStyles) => {
    setCustomStyles(newStyles);
  };

  const value = useMemo(() => ({ styles, updateCustomStyles, customStyles }), [styles, customStyles]);

  return (
    <StyleContext.Provider value={value}>
      {children}
    </StyleContext.Provider>
  );
};

export const useStyles = () => {
  const context = useContext(StyleContext);
  if (!context) {
    throw new Error('useStyles must be used within a StyleProvider');
  }
  return context;
};

